import React, { useEffect, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { Button } from '@mui/material';
import { useModal } from "../../context/ModalContext";
import { REDIRECT_FE_URLS } from '../../utils/Constants';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useLoader } from "../../context/LoaderContext";
import api from "../../services/api";

const GoogleOAuthButton = () => {
  const { oauthLogin } = useAuth();
  const { showLoader, hideLoader } = useLoader();
  const { openErrorModal } = useModal();
  const navigate = useNavigate();

  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const GOOGLE_REDIRECT_URL = process.env.REACT_APP_GOOGLE_REDIRECT_URL
  const GOOGLE_AUTH_URL = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${GOOGLE_REDIRECT_URL}&scope=openid%20email%20profile&access_type=offline`;

  const [codeProcessed, setCodeProcessed] = useState(false);

  const handleOAuthClick = async () => {
    window.location.href = GOOGLE_AUTH_URL;
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authorizationCode = urlParams.get("code");
    const error = urlParams.get("error");
    
    // Log all URL parameters for debugging
    const allParams = {};
    for (const [key, value] of urlParams.entries()) {
      allParams[key] = value;
    }
    if (error) {
      openErrorModal("OAuth Error", `Authentication failed: ${error}`);
      return;
    }
    
    if (authorizationCode && !codeProcessed) {
      setCodeProcessed(true);
      // Clear the "code" parameter from the URL
      const url = new URL(window.location.href);
      url.searchParams.delete("code");
      window.history.replaceState({}, document.title, url.toString());

      handleGoogleAuth(authorizationCode);
    }
  }, [codeProcessed]);


  const handleGoogleAuth = async (authorizationCode) => {
    showLoader("Authenticating...");
    try {
      const tokenResponse = await api.post('auth/exchangetoken/', { authorization_code: authorizationCode });
      
      // Backend returns id_token inside data object
      const { id_token } = tokenResponse.data.data || {};
      
      if (!id_token) {
        throw new Error('Failed to get id_token from backend');
      }

      const signinResponse = await api.post('oauth/signin/', { google_id_token: id_token });
      const result = signinResponse.data;
      console.log('Signin response:', result);

      // Backend returns access_token inside data object
      const accessToken = result.data?.access_token || result.access_token;
      const user = result.data?.user || result.user;

      if (result.success && accessToken) {
        api.setAuthToken(accessToken);
        hideLoader();
        
        // For admin, always redirect to dashboard on successful login
        showLoader("Logging in...");
        return oauthLogin(user, accessToken)
          .then(() => {
            hideLoader();
            navigate("/dashboard"); // Redirect to admin dashboard
          })
          .catch((loginError) => {
            hideLoader();
            openErrorModal("Login Failed", loginError.message || "Failed to complete login process.");
          });
      } else {
        throw new Error(result.message || 'Authentication failed');
      }
    } catch (error) {
      hideLoader();
      openErrorModal("Login Failed", error.response?.data?.message || error.message || "Authentication failed. Please try again.");
    }
  };

  

  return (
    <Button
      fullWidth
      variant="outlined"
      startIcon={<FcGoogle />}
      onClick={handleOAuthClick}
      className="oauth-button google-button"
      sx={{ 
        py: 1,
        mt: 2,
        mb: 2,
        fontSize: '0.875rem',
        color: '#757575',
        backgroundColor: '#ffffff',
        borderColor: '#dadce0',
        borderWidth: '1px',
        borderStyle: 'solid',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        '&:hover': {
          backgroundColor: '#f5f5f5',
          borderColor: '#dadce0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
      }}
    >
      Login with Google
    </Button>
  );
};

export default GoogleOAuthButton;